import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, ChevronRight } from "lucide-react";
import { Header } from "@/components/Header";
import { SiteFooter } from "@/components/SiteFooter";
import { useLang } from "@/context/LangContext";
import { useSeo, SITE, ORG } from "@/lib/seo";
import { GUIDES, guideBySlug } from "@/lib/seoGuides";
import { hubBySlug } from "@/lib/seoHubs";
import { AuthorBio } from "@/pages/HubPage";

const INDEX_SEO = { title: "Guide marketing digitale per PMI in Ticino | AURORA-ADV",
  description: "Guide pratiche di AURORA-ADV per PMI in Ticino: quanto costa la SEO, quanto budget serve per Google Ads e come ottimizzare il profilo Google Business.", path: "/guide",
  jsonld: [{ "@context": "https://schema.org", "@type": "CollectionPage", name: "Guide AURORA-ADV", url: `${SITE}/guide`, inLanguage: "it-CH", publisher: ORG,
    hasPart: GUIDES.map((g) => ({ "@type": "Article", headline: g.h1, url: `${SITE}/guide/${g.slug}` })) }] };

const ld = (g) => [
  { "@context": "https://schema.org", "@type": "Article", headline: g.h1, description: g.description, datePublished: g.date, dateModified: g.date,
    inLanguage: "it-CH", author: ORG, publisher: ORG, mainEntityOfPage: `${SITE}/guide/${g.slug}` },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: g.faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
  { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` }, { "@type": "ListItem", position: 2, name: "Guide", item: `${SITE}/guide` },
    { "@type": "ListItem", position: 3, name: g.h1, item: `${SITE}/guide/${g.slug}` }] },
];

const Crumbs = ({ g }) => (
  <nav aria-label="breadcrumb" data-testid="guide-breadcrumb" className="flex items-center gap-1 text-xs text-[#71717a] mb-8">
    <Link to="/" className="hover:text-[#2B3036]">Home</Link><ChevronRight className="w-3 h-3" />
    {g ? <><Link to="/guide" className="hover:text-[#2B3036]">Guide</Link><ChevronRight className="w-3 h-3" /><span className="text-[#2B3036]">{g.h1}</span></> : <span className="text-[#2B3036]">Guide</span>}
  </nav>
);

const GuideList = ({ items }) => (
  <ul className="grid md:grid-cols-3 gap-px bg-[#cfd6d9] border border-[#cfd6d9]">
    {items.map((x) => (
      <li key={x.slug}><Link to={`/guide/${x.slug}`} data-testid={`guide-link-${x.slug}`} className="group h-full block bg-white p-5 hover:bg-[#09090b] hover:text-white transition-colors">
        <h3 className="font-display text-lg leading-tight">{x.h1}</h3>
        <p className="mt-2 text-xs text-[#5b6168] group-hover:text-[#c4c9ce]">{x.description}</p>
      </Link></li>
    ))}
  </ul>
);

const Article = ({ g }) => {
  const hub = hubBySlug[g.hub];
  return (
    <article data-testid={`guide-${g.slug}`} className="max-w-3xl">
      <h1 data-testid="guide-title" className="font-display text-4xl sm:text-5xl tracking-tighter leading-[1.05]">{g.h1}</h1>
      <p className="mt-3 text-xs font-mono text-[#71717a]">Aggiornata il {g.date.split("-").reverse().join(".")} · Team AURORA-ADV</p>
      <p data-testid="guide-answer" className="mt-6 text-lg leading-relaxed text-[#2B3036] border-l-2 border-[#E0262C] pl-4">{g.answer}</p>
      {g.sections.map((s) => (
        <section key={s.h2} className="mt-10">
          <h2 className="font-display text-2xl tracking-tight">{s.h2}</h2>
          <p className="mt-3 text-[15px] leading-relaxed text-[#3f454c]">{s.p}</p>
          {s.list && <ul className="mt-4 space-y-2">{s.list.map((x) => <li key={x} className="flex gap-2 text-sm"><Check className="w-4 h-4 mt-0.5 text-[#10b981] shrink-0" />{x}</li>)}</ul>}
        </section>
      ))}
      <section className="mt-10">
        <h2 className="font-display text-2xl tracking-tight">Domande frequenti</h2>
        {g.faq.map(([q, a]) => <div key={q} className="mt-4"><h3 className="font-semibold">{q}</h3><p className="mt-1 text-sm text-[#3f454c]">{a}</p></div>)}
      </section>
      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/" data-testid="guide-cta-audit" className="h-12 px-6 inline-flex items-center gap-2 bg-[#E0262C] text-white font-medium hover:bg-[#2B3036] transition-colors">Analizza gratis il tuo sito <ArrowRight className="w-4 h-4" /></Link>
        {hub && <Link to={`/${hub.slug}`} data-testid="guide-hub-link" className="h-12 px-5 inline-flex items-center border border-[#2B3036] hover:bg-white transition-colors">{hub.title.split(" | ")[0]}</Link>}
      </div>
      <AuthorBio />
      <section className="mt-12">
        <h2 className="font-display text-2xl tracking-tight mb-4">Altre guide</h2>
        <GuideList items={GUIDES.filter((x) => x.slug !== g.slug)} />
      </section>
    </article>
  );
};

export default function GuidePage() {
  const { slug } = useParams();
  const g = slug && guideBySlug[slug];
  const { lang, setLang, t } = useLang();
  useSeo(g ? { title: g.title, description: g.description, path: `/guide/${g.slug}`, jsonld: ld(g), lang: "it" } : { ...INDEX_SEO, noindex: !!slug, lang: "it" });
  return (
    <div data-testid="guide-page" className="min-h-screen bg-[#E6EAEC] flex flex-col">
      <Header lang={lang} setLang={setLang} t={t} />
      <main className="max-w-5xl mx-auto w-full px-6 lg:px-10 py-10 flex-1">
        <Crumbs g={g} />
        {g ? <Article g={g} /> : slug ? <p data-testid="guide-not-found">Guida non trovata. <Link to="/guide" className="underline">Tutte le guide</Link></p> : (
          <>
            <h1 data-testid="guide-index-title" className="font-display text-4xl sm:text-5xl tracking-tighter mb-4">Guide pratiche di marketing digitale per PMI in Ticino</h1>
            <p data-testid="guide-index-intro" className="mb-8 max-w-[68ch] text-[15px] leading-relaxed text-[#3f454c]">Risposte concrete alle domande che ci fanno più spesso i titolari di aziende ticinesi: quanto costa farsi trovare su Google, quanto budget serve per la pubblicità online e come sfruttare al meglio il profilo Google Business. Ogni guida riporta prezzi chiari in franchi, esempi pratici ed errori da evitare, ed è scritta dal team tecnico di AURORA-ADV a Lugano. Se vuoi capire da dove partire con il tuo sito, puoi anche fare subito l'audit SEO gratuito.</p>
            <GuideList items={GUIDES} />
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
